import Head from 'next/head';
import { useRouter } from 'next/router';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { MetaEnum } from '@/types/translations/MetaEnum';
import { ImagesBlogBlogThumbnailJpeg } from '@/assets/images_blog.assets';
import { ImagesPressLogosGoldBlueSpiritoryLogoJpg } from '@/assets/images_press_logos_gold-blue.assets';
export default function BlogMeta() {
  const { locale: localeParam = 'en', defaultLocale } = useRouter();
  const { translate } = useTypedTranslation<MetaEnum>('meta');

  const locale = localeParam === defaultLocale ? 'en' : localeParam;

  const image = `https://spiritory.com${ImagesBlogBlogThumbnailJpeg.src}`;

  return (
    <Head>
      <title>{translate(MetaEnum['blog.blog_title'])}</title>
      <meta
        name="keywords"
        content={translate(MetaEnum['blog.blog_keywords'])}
      />
      <meta
        name="description"
        content={translate(MetaEnum['blog.blog_description'])}
      />
      <meta name="subject" content={translate(MetaEnum['blog.blog_subject'])} />
      <meta name="copyright" content="Spiritory.com" />
      <meta name="language" content="ES" />
      <meta name="author" content="Spiritory Team, info@spiritory.com" />
      <meta name="designer" content="Spiritory Development Team" />
      <meta name="image_src" content={image} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Blog',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://spiritory.com/${locale}/blog`,
            },
            headline: translate(MetaEnum['blog.blog_title']),
            description: translate(MetaEnum['blog.blog_description']),
            image: image,
            author: {
              '@type': 'Organization',
              name: 'Spiritory',
              email: 'info@spiritory.com',
            },
            publisher: {
              '@type': 'Organization',
              name: 'Spiritory',
              logo: {
                '@type': 'ImageObject',
                url: `https://spiritory.com${ImagesPressLogosGoldBlueSpiritoryLogoJpg.src}`,
                width: 474,
                height: 474,
              },
            },
            datePublished: '2025-01-14',
            dateModified: new Date().toISOString(),
          }),
        }}
      />

      <meta name="reply-to" content="info@spiritory.com" />
      <meta name="owner" content="Spiritory" />
      <meta name="url" content={`https://spiritory.com/${locale}/blog`} />
      <meta
        name="identifier-URL"
        content={`https://spiritory.com/${locale}/blog`}
      />
      <meta
        name="category"
        content={translate(MetaEnum['blog.blog_category'])}
      />
      <meta name="coverage" content="Worldwide" />
      <meta name="distribution" content="Global" />
      <meta name="rating" content="General" />
      <meta name="revisit-after" content="7 days" />
      <meta
        name="subtitle"
        content={translate(MetaEnum['blog.blog_subtitle'])}
      />
      <meta name="target" content="all" />
      <meta name="HandheldFriendly" content="True" />
      <meta name="MobileOptimized" content="320" />
      <meta name="date" content={new Date().toISOString()} />

      {/* OpenGraph Meta Tags */}
      <meta
        property="og:title"
        content={translate(MetaEnum['blog.blog_og_title'])}
      />
      <meta property="og:type" content="website" />
      <meta
        property="og:url"
        content={`https://spiritory.com/${locale}/blog`}
      />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Spiritory" />
      <meta
        property="og:description"
        content={translate(MetaEnum['blog.blog_description'])}
      />
      <meta property="og:locale" content={locale} />
      <meta property="og:updated_time" content={new Date().toISOString()} />

      {/* Twitter Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Spiritory Blog" />
      <meta
        name="twitter:description"
        content={translate(MetaEnum['blog.blog_twitter_description'])}
      />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@spiritoryGlobal" />
      <meta name="twitter:creator" content="@spiritoryGlobal" />

      {/* Apple Meta Tags */}
      <meta name="apple-mobile-web-app-title" content="Spiritory Blog" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-touch-fullscreen" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black" />

      {/* Security and Verification Tags */}
      <meta name="referrer" content="no-referrer" />

      {/* General Meta Tags */}
      <meta
        httpEquiv="Cache-Control"
        content="no-cache, no-store, must-revalidate"
      />
      <meta httpEquiv="Pragma" content="no-cache" />
      <meta httpEquiv="Expires" content="0" />

      <link rel="canonical" href={`https://spiritory.com/${locale}/blog`} />
      <link rel="shortcut icon" href="/favicon.ico" type="image/x-icon" />
    </Head>
  );
}
